import { useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { LAYER_STEP, useLayer } from './overlayLayer';

// A MENU HUNG UNDER A BUTTON: portalled to <body> so no scroll container clips it, a
// transparent catcher under it that closes it on a click anywhere else, and the panel itself.
// What goes in it is the caller's.
//
// WHERE IT HANGS: under the anchor, left-aligned with it, pulled back onto the screen when
// that would put it off the right edge. Or centred under it (`center`), for an anchor in the
// middle of the screen.
//
// It FOLLOWS the anchor, and it used to be measured once when it opened. The argument for
// measuring once was that the click which scrolls is the click that closes it — true of a
// click, and not true of a wheel or a trackpad, which scroll without clicking anything. So
// the menu sat where the anchor had been and the page moved out from under it.
//
// Tracked on the element rather than handed a rectangle: a rectangle is where something was.
// Capture on the scroll listener because the anchor is usually inside a panel with its own
// overflow, and a scroll inside that panel never reaches window on the way up.
//
// AS WIDE AS ITS WIDEST ROW (`w-max`), so the right-edge clamp has to MEASURE rather than
// compute — and measure again whenever the content changes width, which is what the
// ResizeObserver is for. In a layout effect, so the first placement lands before the browser
// paints and the menu is never seen at the corner it starts at.
const MARGIN = 8;

export function PopMenu({ anchor, onClose, children, center = false }: {
  anchor: RefObject<HTMLElement | null>;
  onClose: () => void;
  children: ReactNode;
  center?: boolean;
}) {
  const box = useRef<HTMLDivElement>(null);
  const [at, setAt] = useState({ top: -9999, left: -9999 });
  const z = useLayer() + LAYER_STEP;
  // Read through a ref, so a caller passing a fresh arrow each render does not re-subscribe.
  const close = useRef(onClose);
  close.current = onClose;

  useLayoutEffect(() => {
    const place = () => {
      const a = anchor.current?.getBoundingClientRect();
      if (!a) return;
      const width = box.current?.offsetWidth ?? 0;
      setAt({
        top: a.bottom + 6,
        left: Math.max(MARGIN, Math.min(center ? a.left + (a.width - width) / 2 : a.left, window.innerWidth - width - MARGIN)),
      });
    };
    place();
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close.current();
    const ro = new ResizeObserver(place);
    if (box.current) ro.observe(box.current);
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    window.addEventListener('keydown', onKey);
    return () => {
      ro.disconnect();
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
      window.removeEventListener('keydown', onKey);
    };
  }, [anchor, center]);

  return createPortal(
    // The catcher is what closes the menu on a click anywhere else — including on the
    // button that opened it, which is how a second press puts it away.
    <div className="fixed inset-0" style={{ zIndex: z }} onClick={onClose}>
      <div
        ref={box}
        style={{ top: at.top, left: at.left }}
        onClick={(e) => e.stopPropagation()}
        className="absolute w-max overflow-hidden rounded-xl border border-slate-200 bg-white py-1 shadow-xl"
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}
